import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";

const API = ""; // same-origin now — Vercel rewrites /api/* to the backend (see client/vercel.json). Keeps the session cookie first-party for Safari/Firefox.

export default function Editor() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const token        = localStorage.getItem("subai_session"); // non-sensitive "am I logged in" flag now — real session is the httpOnly cookie

  const [subs,       setSubs]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState(null);
  const [saved,      setSaved]      = useState(false);
  const [fileInfo,   setFileInfo]   = useState(null);
  const [searchQ,    setSearchQ]    = useState("");
  const [activeIdx,  setActiveIdx]  = useState(null);
  const [downloadUrl,setDownloadUrl]= useState(null);
  const [toast,      setToast]      = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Load subtitle from server ──
  useEffect(() => {
    if (!token) { navigate("/"); return; }
    if (!id) { navigate("/dashboard"); return; }
    loadSubtitle();
  }, [id]);

  const loadSubtitle = async () => {
    setLoading(true);
    setError(null);
    try {
      const res  = await fetch(`${API}/api/subtitle-texts/${id}`, {
        headers: { Authorization: "Bearer " + token }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Load failed");

      // Prefer convertedText (translated) over original
      const subsToEdit = data.subs || [];
      setFileInfo({ ext: data.ext, total: data.total });
      setSubs(subsToEdit);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Edit a single subtitle text ──
  const editSub = useCallback((idx, newText) => {
    setSubs(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], text: newText };
      return next;
    });
    setSaved(false);
    setDownloadUrl(null);
  }, []);

  // ── Save edits to server ──
  const handleSave = async () => {
    setSaving(true);
    try {
      const res  = await fetch(`${API}/api/subtitle/save/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ subtitles: subs })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Save failed");
      setSaved(true);
      setDownloadUrl(data.file);
      showToast("✅ Saved!");
    } catch (e) {
      showToast("❌ " + e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Filtered list for search ──
  const filtered = searchQ.trim()
    ? subs.map((s, i) => ({ ...s, _orig: i }))
        .filter(s => s.text.toLowerCase().includes(searchQ.toLowerCase()) || s.time?.includes(searchQ))
    : subs.map((s, i) => ({ ...s, _orig: i }));

  /* ── Styles ── */
  const S = {
    page:      { minHeight: "100vh", background: "#020c14", color: "#e0e0e0", fontFamily: "monospace", paddingBottom: 80 },
    navbar:    { background: "rgba(0,15,30,0.95)", borderBottom: "1px solid #0d2035", padding: "12px 16px", display: "flex", alignItems: "center", gap: 10, position: "sticky", top: 0, zIndex: 10, backdropFilter: "blur(8px)" },
    backBtn:   { background: "transparent", border: "1px solid #0d2035", borderRadius: 8, color: "#00cfff", padding: "6px 14px", cursor: "pointer", fontSize: 13, fontFamily: "monospace" },
    title:     { color: "#fff", fontWeight: 800, fontSize: 15, flex: 1 },
    saveBtn:   { background: saving ? "#1a3a1a" : "linear-gradient(135deg,#00C896,#007a5a)", border: "none", borderRadius: 8, color: "#fff", padding: "8px 18px", cursor: saving ? "not-allowed" : "pointer", fontWeight: 700, fontSize: 13, fontFamily: "monospace" },
    searchBar: { margin: "12px 16px 4px", background: "#0a1520", border: "1px solid #0d2035", borderRadius: 10, padding: "10px 14px", color: "#00cfff", fontSize: 13, width: "calc(100% - 32px)", boxSizing: "border-box", fontFamily: "monospace", outline: "none" },
    countBar:  { color: "#2a4a5a", fontSize: 11, margin: "4px 16px 8px", letterSpacing: 1 },
    subCard:   { margin: "6px 16px", background: "#050f1a", border: "1px solid #0d2035", borderRadius: 10, overflow: "hidden", transition: "border-color 0.2s" },
    subCardAct:{ margin: "6px 16px", background: "#050f1a", border: "1px solid #00cfff55", borderRadius: 10, overflow: "hidden" },
    subHeader: { display: "flex", justifyContent: "space-between", padding: "6px 12px", background: "#030a12", borderBottom: "1px solid #0d2035" },
    subIdx:    { color: "#1a4060", fontSize: 10, fontWeight: 700 },
    subTime:   { color: "#1a4060", fontSize: 10 },
    textarea:  { width: "100%", background: "transparent", border: "none", color: "#d0e8f0", fontSize: 14, padding: "10px 12px", resize: "none", fontFamily: "monospace", outline: "none", lineHeight: 1.6, boxSizing: "border-box", minHeight: 52 },
    empty:     { textAlign: "center", color: "#1a3a5a", padding: "60px 20px", fontSize: 14 },
    toastWrap: { position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)", background: "#0a2030", border: "1px solid #00cfff44", borderRadius: 10, padding: "10px 20px", color: "#00cfff", fontSize: 13, fontFamily: "monospace", zIndex: 999, whiteSpace: "nowrap", boxShadow: "0 4px 20px #000a" },
    dlBar:     { position: "fixed", bottom: 0, left: 0, right: 0, background: "rgba(0,15,30,0.97)", borderTop: "1px solid #00cfff22", padding: "12px 16px", display: "flex", gap: 10, backdropFilter: "blur(8px)", zIndex: 10 },
    dlBtn:     { flex: 1, padding: "12px", background: "linear-gradient(135deg,#00C896,#007a5a)", border: "none", borderRadius: 10, color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer", textDecoration: "none", textAlign: "center", fontFamily: "monospace" },
    dashBtn:   { padding: "12px 16px", background: "transparent", border: "1px solid #0d2035", borderRadius: 10, color: "#00cfff", fontSize: 13, cursor: "pointer", fontFamily: "monospace" },
  };

  if (loading) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
      <div style={{ width: 40, height: 40, border: "3px solid #0d2035", borderTop: "3px solid #00cfff", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
      <p style={{ color: "#00cfff", fontSize: 13 }}>Subtitle load ho raha hai...</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16, padding: 24 }}>
      <p style={{ color: "#ff4444", fontSize: 14, textAlign: "center" }}>❌ {error}</p>
      <button onClick={() => navigate("/dashboard")} style={S.backBtn}>← Dashboard</button>
    </div>
  );

  return (
    <div style={S.page}>
      {/* Navbar */}
      <div style={S.navbar}>
        <button onClick={() => navigate("/dashboard")} style={S.backBtn}>←</button>
        <span style={S.title}>✏️ Subtitle Editor</span>
        <button onClick={handleSave} disabled={saving} style={S.saveBtn}>
          {saving ? "Saving..." : "💾 Save"}
        </button>
      </div>

      {/* Search */}
      <input
        type="text"
        value={searchQ}
        onChange={e => setSearchQ(e.target.value)}
        placeholder="🔍 Search text ya time..."
        style={S.searchBar}
      />
      <div style={S.countBar}>
        {searchQ ? `${filtered.length} results` : `${subs.length} subtitles`}
        {saved && !searchQ && <span style={{ color: "#00C896", marginLeft: 8 }}>✓ Saved</span>}
      </div>

      {/* Subtitle list */}
      {filtered.length === 0
        ? <div style={S.empty}>Koi subtitle nahi mila 🔍</div>
        : filtered.map((s) => {
            const origIdx = s._orig;
            const isActive = activeIdx === origIdx;
            return (
              <div key={origIdx} style={isActive ? S.subCardAct : S.subCard} onClick={() => setActiveIdx(origIdx)}>
                <div style={S.subHeader}>
                  <span style={S.subIdx}>#{s.index || origIdx + 1}</span>
                  <span style={S.subTime}>{s.time}</span>
                </div>
                <textarea
                  value={s.text}
                  onChange={e => editSub(origIdx, e.target.value)}
                  onFocus={() => setActiveIdx(origIdx)}
                  style={{ ...S.textarea, minHeight: s.text.split("\n").length > 1 ? 72 : 52 }}
                  rows={Math.max(1, s.text.split("\n").length)}
                />
              </div>
            );
          })
      }

      {/* Bottom bar */}
      <div style={S.dlBar}>
        {downloadUrl
          ? <a href={downloadUrl} download target="_blank" rel="noreferrer" style={S.dlBtn}>⬇ Download Edited File</a>
          : <button onClick={handleSave} disabled={saving} style={{ ...S.dlBtn, background: saving ? "#1a3a1a" : "linear-gradient(135deg,#00C896,#007a5a)", cursor: saving ? "not-allowed" : "pointer" }}>
              {saving ? "⏳ Saving..." : "💾 Save Changes"}
            </button>
        }
        <button onClick={() => navigate("/dashboard")} style={S.dashBtn}>🏠</button>
      </div>

      {/* Toast */}
      {toast && <div style={{ ...S.toastWrap, borderColor: toast.type === "error" ? "#ff444455" : "#00cfff44", color: toast.type === "error" ? "#ff4444" : "#00cfff" }}>{toast.msg}</div>}
    </div>
  );
}
